import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Cog6ToothIcon,
  PlusIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ShieldCheckIcon,
  BellIcon,
  LinkIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import {
  SystemSetting,
  SettingCategory,
  SettingHistory,
  SettingValidationRules,
  SETTING_CATEGORIES,
  CATEGORY_COLORS,
  DATA_TYPE_LABELS,
  CreateSettingRequest,
} from '../../types/systemSettings';
import {
  fetchAllSettings,
  createSetting,
  updateSetting,
  deleteSetting,
  fetchSettingHistory,
  exportSettingsAsJson,
  parseImportFile,
  bulkUpdateSettings,
} from '../../services/systemSettingsService';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  SECURITY: <ShieldCheckIcon className="w-5 h-5" />,
  NOTIFICATIONS: <BellIcon className="w-5 h-5" />,
  INTEGRATIONS: <LinkIcon className="w-5 h-5" />,
  FEATURES: <BoltIcon className="w-5 h-5" />,
};

export default function SystemSettingsPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('SETTINGS_EDIT');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Data
  const [settingsByCategory, setSettingsByCategory] = useState<Record<string, SystemSetting[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Active category tab
  const [activeCategory, setActiveCategory] = useState<SettingCategory>('SECURITY');

  // Editing state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSettingRequest>({
    category: 'SECURITY',
    key: '',
    value: '',
    description: '',
    data_type: 'STRING',
    validation_rules: '',
  });
  const [createError, setCreateError] = useState<string | null>(null);

  // History panel
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<SettingHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Import state
  const [importing, setImporting] = useState(false);

  // --- Data loading ---
  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllSettings();
      setSettingsByCategory(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  // Current category settings
  const currentSettings = settingsByCategory[activeCategory] || [];
  const totalSettings = Object.values(settingsByCategory).reduce((sum, arr) => sum + arr.length, 0);

  // --- Validation ---
  const validateValue = (value: string, dataType: string, rulesJson?: string | null): string | null => {
    if (dataType === 'NUMBER') {
      if (isNaN(Number(value))) return 'La valeur doit être un nombre valide';
    }
    if (dataType === 'BOOLEAN') {
      if (value !== 'true' && value !== 'false') return "La valeur doit être 'true' ou 'false'";
    }
    if (dataType === 'JSON') {
      try { JSON.parse(value); } catch { return 'La valeur doit être du JSON valide'; }
    }
    if (rulesJson) {
      try {
        const rules: SettingValidationRules = JSON.parse(rulesJson);
        if (dataType === 'NUMBER') {
          const num = Number(value);
          if (rules.min !== undefined && num < rules.min) return `La valeur doit être ≥ ${rules.min}`;
          if (rules.max !== undefined && num > rules.max) return `La valeur doit être ≤ ${rules.max}`;
        }
        if (rules.allowed_values && rules.allowed_values.length > 0) {
          if (!rules.allowed_values.includes(value)) {
            return `Valeurs autorisées : ${rules.allowed_values.join(', ')}`;
          }
        }
      } catch { /* ignore parse errors */ }
    }
    return null;
  };

  // --- Handlers ---
  const handleStartEdit = (setting: SystemSetting) => {
    setEditingKey(setting.key);
    setEditValue(setting.value);
    setEditError(null);
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
    setEditError(null);
  };

  const handleSaveEdit = async (setting: SystemSetting) => {
    const validationErr = validateValue(editValue, setting.data_type, setting.validation_rules);
    if (validationErr) { setEditError(validationErr); return; }

    setSaving(true);
    setEditError(null);
    try {
      await updateSetting(setting.key, editValue);
      setEditingKey(null);
      setSuccessMsg(`Paramètre "${setting.key}" mis à jour avec succès`);
      await loadSettings();
    } catch (err: any) {
      setEditError(err.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (setting: SystemSetting) => {
    if (!window.confirm(`Supprimer le paramètre "${setting.key}" ? Cette action est irréversible.`)) return;
    try {
      await deleteSetting(setting.key);
      setSuccessMsg(`Paramètre "${setting.key}" supprimé`);
      await loadSettings();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
    }
  };

  const handleCreate = async () => {
    if (!createForm.key.trim()) { setCreateError('La clé est requise'); return; }
    if (!createForm.value.trim()) { setCreateError('La valeur est requise'); return; }
    const validationErr = validateValue(createForm.value, createForm.data_type, createForm.validation_rules);
    if (validationErr) { setCreateError(validationErr); return; }

    setSaving(true);
    setCreateError(null);
    try {
      await createSetting(createForm);
      setShowCreateModal(false);
      setCreateForm({ category: activeCategory, key: '', value: '', description: '', data_type: 'STRING', validation_rules: '' });
      setSuccessMsg('Paramètre créé avec succès');
      await loadSettings();
    } catch (err: any) {
      setCreateError(err.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleViewHistory = async (key: string) => {
    if (historyKey === key) { setHistoryKey(null); return; }
    setHistoryKey(key);
    setHistoryLoading(true);
    try {
      const result = await fetchSettingHistory(key);
      setHistoryData(result.data || []);
    } catch {
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleExport = () => {
    exportSettingsAsJson(settingsByCategory);
    setSuccessMsg('Paramètres exportés avec succès');
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const parsed = await parseImportFile(file);
      const count = Object.keys(parsed).length;
      if (!window.confirm(`Importer et mettre à jour ${count} paramètre(s) ?`)) {
        setImporting(false);
        return;
      }
      await bulkUpdateSettings(parsed);
      setSuccessMsg(`${count} paramètre(s) importé(s) avec succès`);
      await loadSettings();
    } catch (err: any) {
      setError(err.message || "Erreur lors de l'importation");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // --- Helpers ---
  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const parseRulesDisplay = (rulesJson?: string | null): string => {
    if (!rulesJson) return '';
    try {
      const rules: SettingValidationRules = JSON.parse(rulesJson);
      const parts: string[] = [];
      if (rules.min !== undefined) parts.push(`min: ${rules.min}`);
      if (rules.max !== undefined) parts.push(`max: ${rules.max}`);
      if (rules.pattern) parts.push(`pattern: ${rules.pattern}`);
      if (rules.allowed_values?.length) parts.push(`valeurs: ${rules.allowed_values.join(', ')}`);
      return parts.join(' | ');
    } catch { return ''; }
  };

  // --- Value editor by type ---
  const renderValueEditor = (setting: SystemSetting) => {
    if (editingKey !== setting.key) {
      return (
        <span className="text-sm text-gray-900 font-mono">
          {setting.data_type === 'BOOLEAN' ? (
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              setting.value === 'true' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>
              {setting.value === 'true' ? 'Activé' : 'Désactivé'}
            </span>
          ) : setting.data_type === 'JSON' ? (
            <code className="text-xs bg-gray-50 px-2 py-1 rounded block max-w-md truncate">{setting.value}</code>
          ) : (
            setting.value
          )}
        </span>
      );
    }

    // Editing mode
    switch (setting.data_type) {
      case 'BOOLEAN':
        return (
          <select
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            className="border border-blue-300 rounded-md px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="true">Activé (true)</option>
            <option value="false">Désactivé (false)</option>
          </select>
        );
      case 'NUMBER':
        return (
          <input
            type="number"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            className="border border-blue-300 rounded-md px-3 py-1.5 text-sm w-40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
      case 'JSON':
        return (
          <textarea
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            rows={3}
            className="border border-blue-300 rounded-md px-3 py-1.5 text-sm font-mono w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
      default:
        return (
          <input
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            className="border border-blue-300 rounded-md px-3 py-1.5 text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
    }
  };

  // --- Loading skeleton ---
  if (loading && totalSettings === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="flex space-x-4 mb-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-10 bg-gray-200 rounded w-32"></div>)}
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-gray-200 rounded mb-3"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Cog6ToothIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Paramètres Système</h1>
              <p className="text-gray-600 mt-1">{totalSettings} paramètre(s) configuré(s)</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={loadSettings} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" title="Rafraîchir">
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleExport} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" title="Exporter">
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Exporter
            </button>
            {canEdit && (
              <>
                <input ref={fileInputRef} type="file" accept=".json" onChange={handleImport} className="hidden" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  title="Importer"
                >
                  <ArrowUpTrayIcon className="w-4 h-4 mr-1" /> {importing ? 'Import...' : 'Importer'}
                </button>
                <button
                  onClick={() => {
                    setCreateForm({ ...createForm, category: activeCategory });
                    setCreateError(null);
                    setShowCreateModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="w-4 h-4 mr-2" /> Nouveau Paramètre
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Success / Error banners */}
      {successMsg && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-3" />
          <p className="text-sm text-green-700">{successMsg}</p>
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Category Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px" aria-label="Catégories">
            {SETTING_CATEGORIES.map(cat => {
              const count = (settingsByCategory[cat.value] || []).length;
              const isActive = activeCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => { setActiveCategory(cat.value); setEditingKey(null); setHistoryKey(null); }}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{CATEGORY_ICONS[cat.value]}</span>
                  {cat.label}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            {SETTING_CATEGORIES.find(c => c.value === activeCategory)?.description}
          </p>
        </div>
      </div>

      {/* Settings List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="divide-y divide-gray-200">
          {currentSettings.length === 0 ? (
            <div className="text-center py-12">
              <Cog6ToothIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aucun paramètre dans cette catégorie</p>
              {canEdit && (
                <button
                  onClick={() => {
                    setCreateForm({ ...createForm, category: activeCategory });
                    setCreateError(null);
                    setShowCreateModal(true);
                  }}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  <PlusIcon className="w-4 h-4 mr-2" /> Ajouter un paramètre
                </button>
              )}
            </div>
          ) : (
            currentSettings.map(setting => (
              <div key={setting.id} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-sm font-semibold text-gray-900">{setting.key}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[setting.category] || 'bg-gray-100 text-gray-700'}`}>
                        {DATA_TYPE_LABELS[setting.data_type] || setting.data_type}
                      </span>
                    </div>
                    {setting.description && (
                      <p className="text-xs text-gray-500 mt-1">{setting.description}</p>
                    )}
                    {setting.validation_rules && (
                      <p className="text-xs text-gray-400 mt-0.5 italic">
                        Règles : {parseRulesDisplay(setting.validation_rules)}
                      </p>
                    )}
                    <div className="mt-2 flex items-center space-x-4">
                      {renderValueEditor(setting)}
                      {editingKey === setting.key && (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSaveEdit(setting)}
                            disabled={saving}
                            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs rounded-md text-gray-700 hover:bg-gray-50"
                          >
                            Annuler
                          </button>
                        </div>
                      )}
                    </div>
                    {editingKey === setting.key && editError && (
                      <p className="text-xs text-red-600 mt-1">{editError}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleViewHistory(setting.key)}
                      className={`p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 ${historyKey === setting.key ? 'text-blue-600 bg-blue-50' : ''}`}
                      title="Historique des modifications"
                    >
                      <ClockIcon className="w-4 h-4" />
                    </button>
                    {canEdit && editingKey !== setting.key && (
                      <button
                        onClick={() => handleStartEdit(setting)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50"
                        title="Modifier"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => handleDelete(setting)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                        title="Supprimer"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  Dernière modification : {formatDateTime(setting.updated_at)}
                </div>

                {/* Inline History Panel */}
                {historyKey === setting.key && (
                  <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <ClockIcon className="w-4 h-4 mr-1" /> Historique des modifications
                    </h4>
                    {historyLoading ? (
                      <div className="animate-pulse space-y-2">
                        {[1, 2].map(i => <div key={i} className="h-8 bg-gray-200 rounded"></div>)}
                      </div>
                    ) : historyData.length === 0 ? (
                      <p className="text-xs text-gray-500">Aucun historique disponible</p>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {historyData.map(entry => (
                          <div key={entry.id} className="flex items-start text-xs border-l-2 border-blue-300 pl-3 py-1">
                            <div className="flex-1">
                              <span className="text-gray-500">{formatDateTime(entry.changed_at)}</span>
                              <div className="mt-0.5">
                                {entry.old_value ? (
                                  <>
                                    <span className="text-red-600 line-through font-mono">{entry.old_value}</span>
                                    <span className="text-gray-400 mx-1">→</span>
                                    <span className="text-green-600 font-mono">{entry.new_value}</span>
                                  </>
                                ) : (
                                  <span className="text-green-600 font-mono">{entry.new_value}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Setting Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowCreateModal(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Nouveau Paramètre</h3>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              {createError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{createError}</div>
              )}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Catégorie *</label>
                  <select
                    value={createForm.category}
                    onChange={e => setCreateForm({ ...createForm, category: e.target.value as SettingCategory })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {SETTING_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Clé *</label>
                  <input
                    type="text"
                    value={createForm.key}
                    onChange={e => setCreateForm({ ...createForm, key: e.target.value })}
                    placeholder="ex: session_timeout_minutes"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type de données *</label>
                  <select
                    value={createForm.data_type}
                    onChange={e => setCreateForm({ ...createForm, data_type: e.target.value as any })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="STRING">Texte</option>
                    <option value="NUMBER">Nombre</option>
                    <option value="BOOLEAN">Booléen</option>
                    <option value="JSON">JSON</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Valeur *</label>
                  {createForm.data_type === 'BOOLEAN' ? (
                    <select
                      value={createForm.value}
                      onChange={e => setCreateForm({ ...createForm, value: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Sélectionner</option>
                      <option value="true">Activé (true)</option>
                      <option value="false">Désactivé (false)</option>
                    </select>
                  ) : createForm.data_type === 'JSON' ? (
                    <textarea
                      value={createForm.value}
                      onChange={e => setCreateForm({ ...createForm, value: e.target.value })}
                      rows={3}
                      placeholder='{"key": "value"}'
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <input
                      type={createForm.data_type === 'NUMBER' ? 'number' : 'text'}
                      value={createForm.value}
                      onChange={e => setCreateForm({ ...createForm, value: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={createForm.description || ''}
                    onChange={e => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Description du paramètre"
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Règles de validation (JSON, optionnel)</label>
                  <input
                    type="text"
                    value={createForm.validation_rules || ''}
                    onChange={e => setCreateForm({ ...createForm, validation_rules: e.target.value })}
                    placeholder='{"min": 15, "max": 120}'
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Annuler
                </button>
                <button
                  onClick={handleCreate}
                  disabled={saving || !createForm.key.trim() || !createForm.value.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Création...' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
